import { cn } from "./classNames-DkX30VJU.js";
import { X } from "./icons-DDR9dFSr.js";
import { Button } from "./Button-BAMX1iuI.js";
import { ModalDescription, ModalOverlay, ModalTitle } from "./ModalTitle-7vgpfc6B.js";
import { useEffect, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Close, Content, Portal, Root, Trigger } from "@radix-ui/react-dialog";
// src/components/Modal/ModalBody.tsx
/**
* Scrollable body slot for `Modal`. Place form fields, content blocks, or detail panels here.
* Takes available height inside `Modal.Content` and scrolls when content overflows.
*/
const ModalBody = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "modal-body",
	className: cn("tw:flex-1", "tw:overflow-y-auto", "tw:scroll-fade-y", "tw:px-4", "tw:py-1", "tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0", className),
	...props
});
// src/components/Modal/ModalContent.tsx
const ModalContent = ({ size = "medium", align = "center", className, children, onEscapeKeyDown, ...props }) => {
	const handleEscapeKeyDown = (event) => {
		event.stopPropagation();
		onEscapeKeyDown?.(event);
	};
	return /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(ModalOverlay, { children: /* @__PURE__ */ jsx(Content, {
		...props,
		"data-size": size,
		"data-align": align,
		className: cn("tailwindcss-preflight", "tw:group/modal", "tw:fixed", "tw:left-1/2", "tw:-translate-x-1/2", "tw:z-overlay", "tw:top-1/2", "tw:-translate-y-1/2", "tw:data-[align=top]:top-20", "tw:data-[align=top]:translate-y-0", "tw:w-full", "tw:max-w-2xl", "tw:max-h-[calc(100vh-2*theme(spacing.20))]", "tw:flex", "tw:flex-col", "tw:gap-4", "tw:rounded-modal", "tw:bg-card", "tw:text-card-foreground", "tw:[[data-theme=dark]_&]:ring-1", "tw:[[data-theme=dark]_&]:ring-foreground/10", "tw:py-4", "tw:duration-200", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=open]:zoom-in-95", "tw:data-[state=closed]:zoom-out-95", {
			"tw:max-w-[480px]": size === "small",
			"tw:max-w-4xl": size === "large"
		}, className),
		onEscapeKeyDown: handleEscapeKeyDown,
		children
	}) }) });
};
// src/components/Modal/ModalFooter.tsx
/**
* The modal's action row.
*
* - A child that should fill the row carries `tw:w-full` itself; the footer is already full width.
* - tailwind-merge replaces only within a group: `tw:flex-col` keeps `items-center`, so add `tw:items-stretch`.
*/
const ModalFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	className: cn("tw:flex", "tw:items-center", "tw:justify-end", "tw:gap-2", "tw:px-4", className)
});
// src/components/Modal/ModalActions.tsx
/**
* Container for the header's top-right controls — any `actions` passed to `Modal.Header` plus the
* close icon. Sits in the header grid's last column, top-aligned. Mirrors `Alert`'s action slot.
*/
const ModalActions = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:col-start-3", "tw:row-start-1", "tw:self-start", "tw:flex", "tw:items-center", "tw:gap-1", "tw:ml-3", className),
	...props
});
// src/components/Modal/ModalHeader.tsx
/** Modal header: `[icon] | title (children) | actions + close` on row 1, `description` on row 2. */
const ModalHeader = ({ className, icon, description, "aria-description": ariaDescription, actions, hideCloseIcon = false, children, ...props }) => /* @__PURE__ */ jsxs("div", {
	...props,
	"data-slot": "modal-header",
	className: cn("tw:grid", "tw:grid-cols-[auto_1fr_auto]", "tw:items-center", "tw:px-4", className),
	children: [
		icon,
		children && /* @__PURE__ */ jsx(ModalTitle, {
			className: "tw:col-start-2 tw:row-start-1",
			children
		}),
		(actions || !hideCloseIcon) && /* @__PURE__ */ jsxs(ModalActions, { children: [actions, !hideCloseIcon && /* @__PURE__ */ jsx(Close, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(Button, {
				variant: "invisible",
				size: "icon-sm",
				children: [/* @__PURE__ */ jsx(X, {}), /* @__PURE__ */ jsx("span", {
					className: "tw:sr-only",
					children: "Close"
				})]
			})
		})] }),
		(description || ariaDescription) && /* @__PURE__ */ jsx(ModalDescription, {
			className: cn("tw:col-start-1", "tw:col-end-4", "tw:row-start-2", description ? "tw:mt-1" : "tw:sr-only"),
			children: description ?? ariaDescription
		})
	]
});
// src/components/Modal/ModalIcon.tsx
const NAMED_COLORS = /* @__PURE__ */ new Set([
	"orange",
	"blue",
	"red",
	"green",
	"gray",
	"violet"
]);
/**
* Status-coloured icon for `Modal`, authored inside `Modal.Header`; remote images use `Modal.Image`.
*
* - Soft-tinted inline badge in the header grid's first column, next to the title.
* - `color` is categorical (`red` destructive … `violet` agent), fixed across brand themes.
*/
const ModalIcon = ({ color, icon }) => {
	const customColor = color && !NAMED_COLORS.has(color) ? color : void 0;
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "modal-icon",
		className: cn("tw:flex", "tw:shrink-0", "tw:items-center", "tw:justify-center", "tw:[&_svg]:aspect-square", "tw:col-start-1", "tw:row-start-1", "tw:self-start", "tw:mr-3", "tw:size-8", "tw:rounded", "tw:overflow-hidden", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5", {
			"tw:bg-warning-100 tw:text-warning-500 tw:[[data-theme=dark]_&]:bg-warning-900 tw:[[data-theme=dark]_&]:text-warning-200": color === "orange",
			"tw:bg-blue-100 tw:text-blue-600 tw:[[data-theme=dark]_&]:bg-blue-900 tw:[[data-theme=dark]_&]:text-blue-200": color === "blue",
			"tw:bg-green-100 tw:text-green-900 tw:[[data-theme=dark]_&]:bg-green-900 tw:[[data-theme=dark]_&]:text-green-200": color === "green",
			"tw:bg-error-100 tw:text-error-500 tw:[[data-theme=dark]_&]:bg-error-900 tw:[[data-theme=dark]_&]:text-error-200": color === "red",
			"tw:bg-neutral-100 tw:text-neutral-500 tw:[[data-theme=dark]_&]:bg-neutral-700 tw:[[data-theme=dark]_&]:text-neutral-300": color === "gray",
			"tw:bg-purple-100 tw:text-purple-500 tw:[[data-theme=dark]_&]:bg-purple-900 tw:[[data-theme=dark]_&]:text-purple-200": color === "violet"
		}, { "tw:text-white": !!customColor }),
		style: customColor ? { backgroundColor: customColor } : void 0,
		children: icon
	});
};
// src/components/Modal/ModalImage.tsx
const ModalImage = ({ src, alt = "" }) => {
	const [hasImageError, setHasImageError] = useState(false);
	useEffect(() => setHasImageError(false), [src]);
	if (hasImageError) return null;
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "modal-image",
		className: cn("tw:shrink-0", "tw:overflow-hidden", "tw:flex", "tw:col-start-1", "tw:row-start-1", "tw:self-start", "tw:mr-3", "tw:items-center", "tw:justify-center", "tw:rounded", "tw:size-8"),
		children: /* @__PURE__ */ jsx("img", {
			src,
			alt,
			className: "tw:size-full tw:object-contain",
			onError: () => setHasImageError(true)
		})
	});
};
// src/components/Modal/Modal.tsx
/**
* Blocking dialog the user must act on before continuing. Built on Radix Dialog.
*
* - Not for side editing (`Sheet`), anchored confirms (`Popconfirm`) or hover previews (`HoverCard`).
* - `Modal.Content` wraps Header + Body + Footer; a form's `FormBody` goes in Body, its buttons in Footer.
*
* @see RECIPES.md#modal-form
* @see RECIPES.md#confirm-dialog
*/
const Modal = (props) => /* @__PURE__ */ jsx(Root, { ...props });
Modal.Close = Close;
Modal.Trigger = Trigger;
Modal.Content = ModalContent;
Modal.Body = ModalBody;
Modal.Icon = ModalIcon;
Modal.Image = ModalImage;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
Modal.Overlay = ModalOverlay;
export { Modal };

//# sourceMappingURL=Modal-CLu6Eg5p.js.map